/**
 * Rule-driven invoice auto-fill.
 *
 * Given text extracted from a PDF (see `utils/pdfText.ts`) and the detector
 * definitions served by the backend (`/api/invoice-detectors`), produce a
 * suggestion for the invoice form. Everything is intentionally simple and
 * case-by-case: a detector matches by required substrings, then pulls each
 * field out with a regex + a small fixed transform. When a real invoice
 * extracts wrong, add it to the fixtures and tune the detector's TOML.
 */
import { DateTime } from "luxon";
import type {
  DetectorAmountRuleDto,
  DetectorFieldRuleDto,
  DetectorTransform,
  InvoiceDetectorDto,
  PeriodGranularity,
  UnsupportedInvoiceDto,
} from "../api/types";

/** Categorization hints for a future transaction-matching flow. */
export interface SuggestedTransactionInfo {
  expenseType: string;
  group: string;
  categories: string[];
}

/**
 * The detector output, mirroring the shape of the `test-data/*.json` fixtures.
 * Dates are dotted (`YYYY.MM.DD`) and the amount is a raw decimal string — the
 * form layer converts these to the app's real types when applying them.
 */
/**
 * Who the detected invoice is from — exactly one of a payee (a vendor) or a
 * source (one of the user's accounts, for card statements), as the detector
 * declares it. The other key is absent, so the ground-truth JSON names only
 * the one that applies.
 */
export type SuggestedSender =
  | { payee: string; source?: never }
  | { source: string; payee?: never };

export type InvoiceSuggestion = SuggestedSender & {
  name: string;
  dateReceived: string;
  dueDate: string;
  amount: { amount: string; currency: string } | null;
  /** Upper bound of the matching range; set only by detectors with an
   * `amountMax` rule (statements whose paid amount isn't fixed). */
  amountMax: { amount: string; currency: string } | null;
  payToAccount: string | null;
  kid: string | null;
  transactionInfo: SuggestedTransactionInfo | null;
};

export interface DetectionResult {
  detectorId: string;
  detectorLabel: string;
  suggestion: InvoiceSuggestion;
  /** A sensible filename to rename the attachment to (no path). */
  suggestedFilename: string;
}

const applyTransform = (raw: string, transform: DetectorTransform): string => {
  switch (transform) {
    case "norwegianDate": {
      // DD.MM.YY or DD.MM.YYYY -> YYYY.MM.DD (2-digit year assumed 20YY).
      const m = raw.trim().match(/^(\d{2})\.(\d{2})\.(\d{2}(?:\d{2})?)$/);
      if (!m) return raw.trim();
      const [, dd, mm, yy] = m;
      const year = yy.length === 2 ? `20${yy}` : yy;
      return `${year}.${mm}.${dd}`;
    }
    case "norwegianAmount":
      // 1.497,01 or 1 345,75 -> 1497.01 / 1345.75 (drop thousands separators,
      // comma -> decimal point).
      return raw.trim().replace(/[.\s]/g, "").replace(",", ".");
    case "plainAmount":
      // Already decimal-pointed, as some Norwegian issuers print it (Finter):
      // 129.00 -> 129.00. Only the thousands grouping goes, so 1,234.56 and
      // 1 234.56 -> 1234.56. Note this reads a dot as the DECIMAL point, the
      // opposite of norwegianAmount above, which reads it as a thousands
      // separator — picking the wrong one is a silent factor-of-100 error.
      return raw.trim().replace(/[,\s]/g, "");
    case "spacesToDots":
      // 6005 06 72525 -> 6005.06.72525
      return raw.trim().replace(/\s+/g, ".");
    case "norwegianAccount": {
      // A Norwegian account number, however the invoice spaced it, to the
      // canonical dotted form imported transactions carry: 36336354905 or
      // "3633 63 54905" -> 3633.63.54905. Anything that isn't 11 digits isn't an
      // account number, so leave it alone rather than mis-grouping it.
      const digits = raw.replace(/\D/g, "");
      if (digits.length !== 11) return raw.trim();
      return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6)}`;
    }
    case "norwegianCompactDate": {
      // DDMMYY -> YYYY.MM.DD (2-digit year assumed 20YY), e.g. a service-period
      // marker 010126 -> 2026.01.01.
      const m = raw.trim().match(/^(\d{2})(\d{2})(\d{2})$/);
      if (!m) return raw.trim();
      const [, dd, mm, yy] = m;
      return `20${yy}.${mm}.${dd}`;
    }
    case "isoDate": {
      // YYYY-MM-DD -> YYYY.MM.DD, with an optional day: a bare "2026-05" means
      // the whole month, so day 01 (same convention as norwegianTextDate).
      const m = raw.trim().match(/^(\d{4})-(\d{2})(?:-(\d{2}))?$/);
      if (!m) return raw.trim();
      const [, year, month, day] = m;
      return `${year}.${month}.${day ?? "01"}`;
    }
    case "norwegianTextDate": {
      // A Norwegian date written with the month spelled out, with or without a
      // leading day: "3. aug. 2026" -> 2026.08.03, "juli 2026" -> 2026.07.01
      // (a bare month means the whole month, so day 01). Months are keyed by
      // their first three letters, which are unique across all twelve and cover
      // both the abbreviated ("aug.") and full ("august") spellings.
      const m = raw
        .trim()
        .toLowerCase()
        .match(/^(?:(\d{1,2})\.\s*)?([a-zæøå]{3,})\.?\s*(\d{4})$/);
      if (!m) return raw.trim();
      const [, dd, monthName, year] = m;
      const month = NORWEGIAN_MONTHS.indexOf(monthName.slice(0, 3)) + 1;
      if (month === 0) return raw.trim();
      return `${year}.${String(month).padStart(2, "0")}.${(dd ?? "1").padStart(2, "0")}`;
    }
    case "trim":
      return raw.trim();
  }
};

/** Three-letter prefixes of the Norwegian month names, in calendar order. */
const NORWEGIAN_MONTHS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "mai",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "des",
];

/**
 * Bucket a normalized `YYYY.MM.DD` date into the `{period}` name token:
 * `month` -> `YYYY.MM`, `quarter` -> `YYYY.Qn`, `year` -> `YYYY`.
 */
const formatPeriod = (
  dateYmd: string,
  granularity: PeriodGranularity,
): string => {
  const [year, month] = dateYmd.split(".");
  switch (granularity) {
    case "quarter":
      return `${year}.Q${Math.ceil(Number(month) / 3)}`;
    case "year":
      return year;
    case "month":
      return `${year}.${month}`;
  }
};

/**
 * Derive a second date from the one a layout prints: `offsetDays` shifts it by
 * a fixed distance, `dayOfMonth` moves it to a fixed calendar day of its own
 * month (clamped to the month's length). Not a date? Leave it alone — the same
 * fallback the transforms use, so a misconfigured rule degrades to the
 * untouched value rather than to nonsense.
 */
const deriveDate = (
  value: string,
  rule: DetectorFieldRuleDto,
): string | null => {
  const parsed = DateTime.fromFormat(value, "yyyy.MM.dd");
  if (!parsed.isValid) return value;
  const shifted = rule.offsetDays
    ? parsed.plus({ days: rule.offsetDays })
    : parsed;
  const dated =
    rule.dayOfMonth != null
      ? shifted.set({ day: Math.min(rule.dayOfMonth, shifted.daysInMonth) })
      : shifted;
  return dated.toFormat("yyyy.MM.dd");
};

const applyFieldRule = (
  text: string,
  rule: DetectorFieldRuleDto,
): string | null => {
  // A fixed value stands in for a field the layout never prints (see the DTO);
  // it's already canonical, so no transform or offset applies to it.
  if (rule.value != null) return rule.value;
  if (rule.pattern == null || rule.transform == null) return null;
  const match = text.match(new RegExp(rule.pattern));
  if (!match) return null;
  const captured = match[rule.group ?? 1];
  if (captured == null) return null;
  const transformed = applyTransform(captured, rule.transform);
  return rule.offsetDays != null || rule.dayOfMonth != null
    ? deriveDate(transformed, rule)
    : transformed;
};

const applyAmountRule = (
  text: string,
  rule: DetectorAmountRuleDto,
): { amount: string; currency: string } | null => {
  // A fixed value stands in for a sum the layout never prints as a figure (see
  // the DTO); like a field rule's, it's already canonical, so no transform runs.
  // Its currency still comes from the rule, there being no match to read one
  // from — so a fixed value requires the fixed `currency`.
  if (rule.value != null) {
    return rule.currency == null
      ? null
      : { amount: rule.value, currency: rule.currency.trim() };
  }
  if (
    rule.pattern == null ||
    rule.transform == null ||
    rule.amountGroup == null
  )
    return null;
  const match = text.match(new RegExp(rule.pattern));
  if (!match) return null;
  const value = match[rule.amountGroup];
  // The currency comes from a capture group when the invoice prints an ISO code,
  // otherwise from the rule's fixed `currency` (a total printed as "2 369,07 kr"
  // has no code to capture).
  const currency =
    rule.currencyGroup != null ? match[rule.currencyGroup] : rule.currency;
  if (value == null || currency == null) return null;
  return {
    amount: applyTransform(value, rule.transform),
    currency: currency.trim(),
  };
};

const fillTemplate = (template: string, vars: Record<string, string>): string =>
  template.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? "");

/**
 * The sender a detector declares. The server guarantees exactly one of `payee`
 * and `source`; should a detector ever carry neither, the source comes out
 * empty and the review simply proposes no sender.
 */
const detectorSender = (detector: InvoiceDetectorDto): SuggestedSender =>
  detector.payee != null
    ? { payee: detector.payee }
    : { source: detector.source ?? "" };

/**
 * The fewest non-whitespace characters a PDF must yield before it's worth
 * running any detector. A scan, a screenshot, or an image-exported invoice has
 * no text layer, so extraction returns almost nothing — an invoice shared from
 * an app as a picture measured 11 characters (just its "Download PDF" button),
 * whereas the smallest real invoice we've seen yields ~1140. 40 sits deep in
 * that gap: far above the handful an image leaks, far below any real invoice.
 */
export const MIN_READABLE_TEXT_CHARS = 40;

/**
 * Whether the extracted `text` has enough real content to bother matching
 * detectors against. When this is false the PDF has no usable text layer (it's a
 * scan/image), so the caller should tell the user to enter the invoice by hand
 * rather than run detection and land on the misleading "couldn't recognise it,
 * add a detector" — there's nothing to recognise.
 */
export const hasReadableText = (text: string): boolean =>
  text.replace(/\s+/g, "").length >= MIN_READABLE_TEXT_CHARS;

/**
 * Run the extracted `text` through the `detectors` and return the first match,
 * or `null` when nothing recognises it.
 */
export const detectInvoice = (
  text: string,
  detectors: InvoiceDetectorDto[],
): DetectionResult | null => {
  for (const detector of detectors) {
    if (!detector.mustContain.every((needle) => text.includes(needle))) {
      continue;
    }

    const dateReceived = applyFieldRule(text, detector.fields.dateReceived);
    const dueDate = applyFieldRule(text, detector.fields.dueDate);
    // The two dates are the minimum viable suggestion; without them, treat the
    // detector as a non-match rather than emitting a half-empty form.
    if (dateReceived == null || dueDate == null) continue;

    const payToAccount = detector.fields.payToAccount
      ? applyFieldRule(text, detector.fields.payToAccount)
      : null;
    const kid = detector.fields.kid
      ? applyFieldRule(text, detector.fields.kid)
      : null;
    const amount = detector.amount
      ? applyAmountRule(text, detector.amount)
      : null;
    const amountMax = detector.amountMax
      ? applyAmountRule(text, detector.amountMax)
      : null;

    // `{period}` names the span the invoice is *for*. By default that's the
    // received month; a detector with a `period` field points it at a date
    // inside the billed service period instead (bucketed per granularity).
    const periodSource =
      (detector.fields.period &&
        applyFieldRule(text, detector.fields.period)) ||
      dateReceived;
    const period = formatPeriod(
      periodSource,
      detector.periodGranularity ?? "month",
    );
    const templateVars: Record<string, string> = {
      period,
      dateReceived,
      dueDate,
      kid: kid ?? "",
    };
    const name = fillTemplate(detector.nameTemplate, templateVars);
    const suggestedFilename = fillTemplate(detector.filenameTemplate, {
      ...templateVars,
      name,
    });

    const transactionInfo: SuggestedTransactionInfo | null =
      detector.transactionInfo
        ? {
            expenseType: detector.transactionInfo.expenseType,
            group: detector.transactionInfo.group,
            categories: detector.transactionInfo.categories,
          }
        : null;

    return {
      detectorId: detector.id,
      detectorLabel: detector.label,
      suggestedFilename,
      suggestion: {
        name,
        dateReceived,
        dueDate,
        amount,
        amountMax,
        ...detectorSender(detector),
        payToAccount,
        kid,
        transactionInfo,
      },
    };
  }

  return null;
};

/** A recognised invoice layout that we can't auto-fill (see the DTO doc). */
export interface UnsupportedMatch {
  id: string;
  label: string;
  reason: string;
  /**
   * The detector that must recognise a replacement file, when this match came
   * from a variant nested under one — which is what lets the UI offer to swap
   * the unreadable copy for the original. `null` for a top-level unsupported
   * layout: with no detector there is nothing that could validate a
   * replacement, so no offer is made.
   */
  replacementDetectorId: string | null;
}

/**
 * Check whether the extracted `text` matches an invoice we can recognise but
 * not parse. Returns the reason to surface to the user, or `null` when none
 * match. Run this only after {@link detectInvoice} returns `null`, so a real
 * detector always wins.
 *
 * Nested variants are checked before top-level entries: they carry a detector
 * id, so a match there is actionable rather than merely explanatory.
 */
export const detectUnsupportedInvoice = (
  text: string,
  detectors: InvoiceDetectorDto[],
  unsupported: UnsupportedInvoiceDto[],
): UnsupportedMatch | null => {
  for (const detector of detectors) {
    // `unreadable` is optional on the wire (a serde default), and most
    // detectors declare none.
    for (const variant of detector.unreadable ?? []) {
      if (variant.mustContain.every((needle) => text.includes(needle))) {
        return {
          // Namespaced: a variant id is only unique within its detector.
          id: `${detector.id}/${variant.id}`,
          label: detector.label,
          reason: variant.reason,
          replacementDetectorId: detector.id,
        };
      }
    }
  }
  for (const entry of unsupported) {
    if (entry.mustContain.every((needle) => text.includes(needle))) {
      return {
        id: entry.id,
        label: entry.label,
        reason: entry.reason,
        replacementDetectorId: null,
      };
    }
  }
  return null;
};

/**
 * The outcome of checking a file offered as a replacement for an unreadable
 * copy. Anything but `ok` leaves the stored attachment untouched.
 */
export type ReplacementCheck =
  | { status: "ok"; result: DetectionResult }
  /** No text layer at all — a scan, a screenshot, an image export. */
  | { status: "noTextLayer" }
  /**
   * The same kind of broken copy again. Scrambled text is still *text*, so this
   * can't be told apart from a wrong file by character count — it's recognised
   * by the unreadable variant matching a second time, which is worth saying
   * plainly rather than claiming they picked someone else's invoice.
   */
  | { status: "stillUnreadable" }
  /**
   * Readable, but not the invoice we were replacing. `detectorId` /
   * `detectorLabel` name what it turned out to be, or are `null` when nothing
   * recognised it — enough to tell the user whether they picked the wrong file
   * or hit a gap in our detectors.
   */
  | {
      status: "wrongInvoice";
      detectorId: string | null;
      detectorLabel: string | null;
    };

/**
 * Validate a candidate replacement for the invoice owned by
 * `requiredDetectorId`. The swap is allowed **only** when that exact detector
 * claims the new text: a file that merely happens to be readable, or that turns
 * out to be a different invoice, must not overwrite the stored one.
 *
 * `requiredDetectorId` is `null` when the stored copy is a *picture* — an iOS
 * share export with no text layer, which names no vendor and so can't say which
 * detector the replacement ought to match (see {@link hasReadableText}). There
 * is nothing to compare the candidate against, so the bar is that some detector
 * reads it: that keeps the swap to files we understand, and the user confirms
 * what was detected in the review dialog straight after.
 */
export const checkReplacement = (
  text: string,
  detectors: InvoiceDetectorDto[],
  unsupported: UnsupportedInvoiceDto[],
  requiredDetectorId: string | null,
): ReplacementCheck => {
  if (!hasReadableText(text)) return { status: "noTextLayer" };
  // A real detector always wins, so try that before concluding it's broken.
  const result = detectInvoice(text, detectors);
  if (
    result != null &&
    (requiredDetectorId == null || result.detectorId === requiredDetectorId)
  ) {
    return { status: "ok", result };
  }
  if (
    result == null &&
    detectUnsupportedInvoice(text, detectors, unsupported)
  ) {
    return { status: "stillUnreadable" };
  }
  return {
    status: "wrongInvoice",
    detectorId: result?.detectorId ?? null,
    detectorLabel: result?.detectorLabel ?? null,
  };
};
